import React from "react";
import { Dropdown, DropdownButton } from "react-bootstrap";
import "../assets/styles/pagination.scss";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

const PaginationComponent = ({
  onPageChange,
  itemsPerPage,
  onItemsPerPageChange,
  itemsPerPageOptions,
  dataCount = 0,
  totalCount,
  itemsCount,
  pageNumber,
  totalPages,
}) => {
  // Maintain internal page state since backend no longer returns currentPage/totalPages
  const [page, setPage] = React.useState(1);

  // --- Remembered values: only update when backend actually sends data (page 1 / filter change) ---
  const [rememberedTotalCount, setRememberedTotalCount] = React.useState(null);
  const [rememberedTotalPages, setRememberedTotalPages] = React.useState(null);

  const normalizedDataCount = Number.isFinite(Number(dataCount))
    ? Number(dataCount)
    : 0;
  const incomingItemsCount =
    Number.isFinite(Number(itemsCount)) && Number(itemsCount) >= 0
      ? Number(itemsCount)
      : null;
  const incomingTotalCountProp =
    Number.isFinite(Number(totalCount)) && Number(totalCount) >= 0
      ? Number(totalCount)
      : null;
  const incomingTotalCount =
    incomingItemsCount !== null ? incomingItemsCount : incomingTotalCountProp;

  const incomingTotalPages =
    Number.isFinite(Number(totalPages)) && Number(totalPages) > 0
      ? Number(totalPages)
      : null;

  // Update remembered values only when fresh data arrives (non-null)
  React.useEffect(() => {
    if (incomingTotalCount !== null) {
      setRememberedTotalCount(incomingTotalCount);
    }
  }, [incomingTotalCount]);

  React.useEffect(() => {
    if (incomingTotalPages !== null) {
      setRememberedTotalPages(incomingTotalPages);
    }
  }, [incomingTotalPages]);

  // Use remembered (cached) values – they persist across pages where backend returns null
  const resolvedTotalCount = incomingTotalCount ?? rememberedTotalCount;
  const resolvedTotalPages = incomingTotalPages ?? rememberedTotalPages;
  const hasTotalCount = resolvedTotalCount !== null;

  const effectivePage =
    Number.isFinite(Number(pageNumber)) && Number(pageNumber) > 0
      ? Number(pageNumber)
      : page;

  React.useEffect(() => {
    if (!Number.isFinite(Number(pageNumber))) return;
    const incomingPage = Number(pageNumber);
    if (incomingPage > 0 && incomingPage !== page) {
      setPage(incomingPage);
    }
  }, [pageNumber, page]);

  const rangeStart =
    hasTotalCount && resolvedTotalCount === 0
      ? 0
      : (effectivePage - 1) * itemsPerPage + 1;
  const rangeEnd = hasTotalCount
    ? resolvedTotalCount === 0
      ? 0
      : Math.min(effectivePage * itemsPerPage, resolvedTotalCount)
    : normalizedDataCount;

  const handleItemsPerPageChange = (value) => {
    const parsed = Number(value);
    onItemsPerPageChange(Number.isFinite(parsed) ? parsed : value);
    setPage(1);
  };

  const handlePageChange = (direction) => {
    const next =
      direction === "first"
        ? 1
        : direction === "prev"
          ? Math.max(1, effectivePage - 1)
          : direction === "last"
            ? resolvedTotalPages ?? effectivePage
          : effectivePage + 1;
    const boundedNext =
      resolvedTotalPages !== null ? Math.min(next, resolvedTotalPages) : next;

    if (next < 1 || boundedNext === effectivePage) return;
    setPage(boundedNext);
    onPageChange && onPageChange(boundedNext);
  };

  const handlePageSelect = (targetPage) => {
    const next = Number(targetPage);
    if (!Number.isFinite(next) || next < 1 || next === effectivePage) return;

    const boundedNext =
      resolvedTotalPages !== null ? Math.min(next, resolvedTotalPages) : next;

    setPage(boundedNext);
    onPageChange && onPageChange(boundedNext);
  };

  const pageJumpTargets = React.useMemo(() => {
    if (resolvedTotalPages === null || resolvedTotalPages <= 0) return [];
    const last = resolvedTotalPages;
    const cur = effectivePage;

    // 7 or fewer pages — show all
    if (last <= 7) {
      return Array.from({ length: last }, (_, i) => i + 1);
    }

    // Near the start: 1 2 3 4 5 ... last
    if (cur <= 4) {
      return [1, 2, 3, 4, 5, "ellipsis", last];
    }

    // Near the end: 1 ... last-4 last-3 last-2 last-1 last
    if (cur >= last - 3) {
      return [1, "ellipsis", last - 4, last - 3, last - 2, last - 1, last];
    }

    // Middle: 1 ... cur-1 cur cur+1 ... last
    return [1, "ellipsis-left", cur - 1, cur, cur + 1, "ellipsis-right", last];
  }, [effectivePage, resolvedTotalPages]);

  // Determine last page using totalCount when available, otherwise heuristics.
  const isLastPage = hasTotalCount
    ? resolvedTotalPages !== null
      ? effectivePage >= resolvedTotalPages
      : effectivePage * itemsPerPage >= resolvedTotalCount
    : normalizedDataCount === 0
      ? true
      : normalizedDataCount < itemsPerPage;

  // Auto-correct if we navigated past available data (empty result on page >1)
  React.useEffect(() => {
    if (!hasTotalCount && effectivePage > 1 && normalizedDataCount === 0) {
      const prev = effectivePage - 1;
      setPage(prev);
    }
  }, [effectivePage, hasTotalCount, normalizedDataCount]);

  React.useEffect(() => {
    if (!hasTotalCount) return;

    const maxPage =
      resolvedTotalPages !== null
        ? resolvedTotalPages
        : Math.max(1, Math.ceil(resolvedTotalCount / itemsPerPage));

    if (effectivePage > maxPage) {
      setPage(maxPage);
    }
  }, [
    effectivePage,
    hasTotalCount,
    itemsPerPage,
    resolvedTotalCount,
    resolvedTotalPages,
  ]);

  return (
    <div className="pagination-container">
      <div className="pagination-section pagination-section--size">
        <span className="pagination-label">
          {hasTotalCount
            ? `Wyświetlanie rekordów od ${rangeStart} do ${rangeEnd} z ${resolvedTotalCount}`
            : "Pokazywane elementy"}
        </span>
        <DropdownButton
          id="dropdown-items-per-page"
          title={
            <>
              {itemsPerPage} <i className="bi bi-caret-down-fill"></i>
            </>
          }
          variant="light"
          className="pagination-dropdown"
          onSelect={handleItemsPerPageChange}
        >
          {itemsPerPageOptions.map((option) => (
            <Dropdown.Item key={option} eventKey={option}>
              {option}
            </Dropdown.Item>
          ))}
        </DropdownButton>
                <span className="pagination-label">
          {"rekordów na stronę"}
        </span>
      </div>

      <div className="pagination-section pagination-section--nav">
        {/* << */}
        <button
          className="btn btn-light"
          onClick={() => handlePageChange('first')}
          disabled={effectivePage === 1}
          aria-label="First page"
        >
          <i className="bi bi-chevron-double-left"></i>
        </button>
        {/* < */}
        <button
          className="btn btn-light"
          onClick={() => handlePageChange('prev')}
          disabled={effectivePage === 1}
          aria-label="Previous page"
        >
          <i className="bi bi-chevron-left"></i>
        </button>

        {/* 1, 2, 3, 4, 5, ..., last */}
        {resolvedTotalPages !== null && pageJumpTargets.length > 0 &&
          pageJumpTargets.map((target, index) =>
            String(target).startsWith("ellipsis") ? (
              <span
                key={`ellipsis-${index}`}
                className="pagination-ellipsis"
                aria-hidden="true"
              >
                …
              </span>
            ) : (
              <button
                key={`page-${target}`}
                className={`btn pagination-page-btn${
                  target === effectivePage ? " is-active" : " btn-light"
                }`}
                onClick={() => handlePageSelect(target)}
                disabled={target === effectivePage}
                aria-label={`Strona ${target}`}
              >
                {target}
              </button>
            ),
          )}

        {/* > */}
        <button
          className="btn btn-light"
          onClick={() => handlePageChange('next')}
          disabled={isLastPage}
          aria-label="Next page"
        >
          <i className="bi bi-chevron-right"></i>
        </button>
        {/* >> */}
        <button
          className="btn btn-light"
          onClick={() => handlePageChange('last')}
          disabled={isLastPage || resolvedTotalPages === null}
          aria-label="Last page"
        >
          <i className="bi bi-chevron-double-right"></i>
        </button>

        {/* <span className="pagination-label ms-2">
          {resolvedTotalPages !== null
            ? `Strona ${effectivePage} z ${resolvedTotalPages}`
            : `Strona ${effectivePage}`}
        </span> */}
      </div>
    </div>
  );
};

export default PaginationComponent;